export type StepState = 'pending' | 'active' | 'done' | 'error' | 'warning';

export interface ImportStep {
  id: 'authorized' | 'creating' | 'importing' | 'identifying' | 'completed';
  label: string;
  description: string;
  state: StepState;
}

/** The onboarding-only 5th step: after the data import lands, the review groups
 * for the Categorias step are identified before the flow lets the user move on.
 * 'failed' = the recompute errored — shown as a warning with a retry, never
 * silently promoted to done. `undefined` keeps the original four-step shape
 * (Connections page). */
export type ImportIdentification = 'pending' | 'active' | 'done' | 'failed';

export interface ImportAlert {
  tone: 'amber' | 'rose';
  title: string;
  message: string;
  cta?: { label: string; onClick: () => void };
}

export interface DeriveStepsArgs {
  pendingPluggyItemId: string | null;
  pendingItemInDb: { accounts: { id: string }[]; status?: string } | null;
  activeSync: boolean;
  /** The DATA import finished (accounts landed, sync status settled). With
   * `identification` present the run as a whole is only done once that step
   * reports 'done' too. */
  completed: boolean;
  actionRequired: boolean;
  errored: boolean;
  timedOut: boolean;
  identification?: ImportIdentification;
}

export function deriveImportSteps(args: DeriveStepsArgs): ImportStep[] {
  const withIdentification = args.identification !== undefined;
  const steps: ImportStep[] = [
    {
      id: 'authorized',
      label: 'Autorização concluída',
      description: 'Conexão autorizada com sua instituição.',
      state: 'done',
    },
    {
      id: 'creating',
      label: 'Vinculando conta',
      description: 'Registrando seu acesso ao Vertice.',
      state: 'pending',
    },
    {
      id: 'importing',
      label: 'Importando dados',
      description: 'Sincronizando contas, transações, faturas e investimentos.',
      state: 'pending',
    },
    ...(withIdentification
      ? [{
          id: 'identifying',
          label: 'Identificando grupos',
          description: 'Separando os grupos de lançamentos para a sua revisão.',
          state: 'pending',
        } satisfies ImportStep]
      : []),
    {
      id: 'completed',
      label: 'Tudo pronto',
      description: 'Seus dados já estão disponíveis no painel.',
      state: 'pending',
    },
  ];
  const at = (id: ImportStep['id']): ImportStep => steps.find((s) => s.id === id)!;

  if (args.completed) {
    if (!withIdentification || args.identification === 'done') {
      return steps.map((s) => ({ ...s, state: 'done' }));
    }
    // Data landed but the review groups are not identified — the run must not
    // claim 'Tudo pronto' about a step 2 that has nothing (or stale data) to
    // show. A failed recompute is a warning, not a silent success.
    for (const id of ['authorized', 'creating', 'importing'] as const) at(id).state = 'done';
    at('identifying').state = args.identification === 'failed' ? 'warning' : 'active';
    return steps;
  }

  const itemInDb = !!args.pendingItemInDb;
  const accountsArrived = (args.pendingItemInDb?.accounts.length ?? 0) > 0;

  if (itemInDb) {
    at('creating').state = 'done';
    if (args.actionRequired) {
      at('importing').state = 'warning';
    } else if (args.errored) {
      at('importing').state = 'error';
    } else if (!accountsArrived || args.activeSync) {
      // Accounts can land while the sync status is still active — the import
      // IS still running, and identification only starts once it settles.
      // Advancing here would claim (and on timeout, blame) a step that has
      // not begun.
      at('importing').state = 'active';
    } else {
      at('importing').state = 'done';
      at(withIdentification ? 'identifying' : 'completed').state = 'active';
    }
  } else if (args.pendingPluggyItemId) {
    if (args.errored) at('creating').state = 'error';
    else at('creating').state = 'active';
  }

  if (args.timedOut) {
    const active = steps.find((s) => s.state === 'active');
    if (active) active.state = 'warning';
  }

  return steps;
}

export function buildTimeoutAlert(onRefresh: () => void): ImportAlert {
  return {
    tone: 'amber',
    title: 'Demorando mais do que o esperado',
    message: 'A autorização foi concluída, mas os dados ainda podem levar alguns minutos. Tente atualizar.',
    cta: { label: 'Atualizar', onClick: onRefresh },
  };
}

export function buildActionRequiredAlert(message?: string | null, onRetry?: () => void): ImportAlert {
  return {
    tone: 'amber',
    title: 'Ação necessária',
    message: message ?? 'A instituição pediu uma etapa extra para concluir a conexão.',
    cta: onRetry ? { label: 'Retomar', onClick: onRetry } : undefined,
  };
}

export function buildIdentificationFailedAlert(onRetry: () => void): ImportAlert {
  return {
    tone: 'amber',
    title: 'Não conseguimos preparar os grupos',
    message: 'A importação terminou, mas a identificação dos grupos para revisão falhou. Tente novamente — ou continue e revise depois no app.',
    cta: { label: 'Tentar novamente', onClick: onRetry },
  };
}

export function buildErrorAlert(message?: string | null, onRetry?: () => void): ImportAlert {
  return {
    tone: 'rose',
    title: 'Não foi possível concluir',
    message: message ?? 'Tente novamente em alguns instantes.',
    cta: onRetry ? { label: 'Tentar novamente', onClick: onRetry } : undefined,
  };
}
